import Vue from 'vue'
import Vuex from 'vuex'
import router from './router'
import createLogger from 'vuex/dist/logger'

// Import modules
import DashboardModule from './modules/dashboard/store'
import OrdersSalesModule from './modules/orders-sales/store'
import LogBooksModule from './modules/logbooks/store'
import WebsiteModule from './modules/website/store'
import PromotionsModule from './modules/promotions/store'
import InventoryModule from './modules/inventory/store'
import POSModule from './modules/pos/store'
import CustomersModule from './modules/customers/store'
import RangeMembershipsModule from './modules/range_memberships/store'
import ReportsModule from './modules/reports/store'

let developmentEnvironment = process.env.NODE_ENV !== 'production'
let sandboxEnvironment = document.location.hostname.indexOf('sandbox') > -1
let apiHost = ''
if (developmentEnvironment) {
	apiHost = 'https://cdsapi.test:8086'
} else {
	apiHost = 'https://' + document.location.hostname + '/api'
}
let api = apiHost + '/api.php'
let downloadURL = apiHost + '/download.php'

let pdfHostname = ''
if (developmentEnvironment) {
	pdfHostname = 'https://cdspdf.test:8087'
} else if (sandboxEnvironment) {
	pdfHostname = 'https://pdf.cdssandbox.com'
} else {
	pdfHostname = 'https://securepdf.flightdeck.live'
}
let pdfapi = pdfHostname + '/api.php'

let re = new RegExp("dashboard_token=([^;]+)");
let cookieToken = re.exec(document.cookie)
let dashbordToken = (cookieToken != null) ? unescape(cookieToken[1]) : null

let kioskModeFromPathname = document.location.pathname.indexOf('customer-4473') > -1

Vue.use(Vuex)

const store = new Vuex.Store({
	state: {
		appVersion: '2.5.3',
		siteBrand: '',
		devEnvironment: developmentEnvironment || sandboxEnvironment,
		apiHostname: apiHost,
		apiEndpoint: api,
		pdfAPIEndpoint: pdfapi,
		dashboardToken: dashbordToken,
		downloadEndpoint: downloadURL,
		kioskMode: kioskModeFromPathname,
		loginRoute: '/login',
		loginRequired: true,
		loading: [],
		moduleNavMenu: {},
		source: 'DASHBOARD',
		client: {},
		userHome: '/',
		user: {},
		client_id: null,
		server_address: null,
		server_name: null,
		openDropdown: null,
		flightDeckMainMenu: null,
		flightDeckMetaMenu: null,
		shoppingCartCount: null,
		showingOfflineLogBooks: false,
		menu_item_policies: {},
		menu_item_policies_by_link: {},
		phone_number_types: [],
		address_types: [],
		custom_fields: [],
		customModuleName: '',
		customRouteName: '',
		devNote: '',
		routeLastRequested: '',
		INCLUDE_UPCS_IN_LOGBOOK_EXPORT: null,
		ELECTRONIC_STORAGE_VARIANCE: null,
		ENABLE_SCREEN_CAPTURE_SIGNATURE: null,
		new_feature: null,
		hostname: window.location.hostname
	},
	mutations: {
		UPDATE_DEV_NOTE(state, value) {
			state.devNote = value
		},
		UPDATE_NEW_FEATURE(state, value) {
			state.new_feature = value
		},
		UPDATE_CUSTOM_MODULE_NAME(state, value) {
			state.customModuleName = value
		},
		UPDATE_CUSTOM_ROUTE_NAME(state, value) {
			state.customRouteName = value
		},
		UPDATE_SITE_BRAND(state, value) {
			state.siteBrand = value
		},
		UPDATE_LOGIN_REQUIRED(state, value) {
			state.loginRequired = value
		},
		UPDATE_STATE(state, obj) {
			for (let prop in state) {
				if (obj.hasOwnProperty(prop) && prop != 'logbooks') {
					state[prop] = obj[prop]
				}
			}
		},
		UPDATE_LOADING(state, { value, remove }) {
			if (remove) {
				let index = state.loading.indexOf(value)
				state.loading.splice(index, 1)
			} else if (state.loading.indexOf(value) == -1) {
				state.loading.push(value)
			}
		},
		UPDATE_SERVER_NAME(state, value) {
			state.server_name = value
		},
		UPDATE_OPEN_DROPDOWN(state, value) {
			state.openDropdown = (state.openDropdown != value) ? value : null
		},
		UPDATE_FLIGHT_DECK_MAIN_MENU(state, value) {
			state.flightDeckMainMenu = value
		},
		UPDATE_FLIGHT_DECK_META_MENU(state, value) {
			state.flightDeckMetaMenu = value
		},
		UPDATE_SHOPPING_CART_COUNT(state, value) {
			state.shoppingCartCount = value
		},
		UPDATE_PROPERTY(state, { property, value }) {
			state[property] = value
		},
		UPDATE_ROUTE_LAST_REQUESTED(state, value) {
			state.routeLastRequested = value
		}
	},
	getters: {
		getGlobalSource: state => state.source,
		getDashboardToken: state => state.dashboardToken,
		getDevNote: state => state.devNote,
		getCustomModuleName: state => state.customModuleName,
		getCustomRouteName: state => state.customRouteName,
		getSiteBrand: state => state.siteBrand,
		getImageUploadAPI: () => api + '?action=upload_site_image',
		getLoading: state => state.loading.length > 0,
		getDevEnvironment: state => state.devEnvironment,
		getApiHost: state => state.apiHostname,
		getApiEndpoint: state => state.apiEndpoint,
		getPDFAPIEndpoint: state => state.pdfAPIEndpoint,
		getDownloadEndpoint: state => state.downloadEndpoint,
		getClient: state => state.client,
		getClientId: state => state.client_id,
		getClientTypeCode: state => state.client.client_type_code,
		getServerAddress: state => state.server_address,
		getUserHome: state => state.userHome,
		getUser: state => state.user,
		getServerName: state => state.server_name,
		getLoginRoute: state => state.loginRoute,
		getOpenDropdown: state => state.openDropdown,
		getFlightDeckMainMenu: state => state.flightDeckMainMenu,
		getFlightDeckMetaMenu: state => state.flightDeckMetaMenu,
		getModuleNavMenu: state => state.moduleNavMenu,
		getShowingOfflineLogBooks: state => state.showingOfflineLogBooks,
		getIncludeUPCsInLogbookExport: state => state.INCLUDE_UPCS_IN_LOGBOOK_EXPORT,
		getElectronicStorageVariance: state => state.ELECTRONIC_STORAGE_VARIANCE,
		getEnableScreenCaptureSignature: state => state.ENABLE_SCREEN_CAPTURE_SIGNATURE,
		getShoppingCartCount: state => state.shoppingCartCount,
		getMenuItemPolicies: state => state.menu_item_policies,
		getMenuItemPoliciesByLink: state => state.menu_item_policies_by_link,
		getLoginRequired: state => state.loginRequired,
		getPhoneNumberTypes: state => state.phone_number_types,
		getAddressTypes: state => state.address_types,
		getCustomFields: state => state.custom_fields,
		getRouteLastRequested: state => state.routeLastRequested,
		getAppVersion: state => state.appVersion,
		getNewFeature: state => state.new_feature,
		getAdministratorFlag(state) {
			var userTypeCode = (state.user) ? state.user.user_type_code : localStorage.getItem('user_type_code')
			return userTypeCode == 'ADMIN' || userTypeCode == 'SUPERADMIN'
		},
		getIsPrimaryDomain(state) {
			return state.hostname == state.client.primary_domain
		}
	},
	actions: {
		bootstrap({ state, commit, dispatch }) {
			if (state.loading.indexOf('fetchClient') == -1) {
				commit('UPDATE_LOADING', { value: 'fetchClient' })
				dispatch('apiCall', { destination: 'get_client', data: {} }).then(obj => {
					commit('UPDATE_LOADING', { value: 'fetchClient', remove: true })
					if (state.user.user_name) {
						this.dispatch('fetchFlightDeckMenus')
					}
				}).catch(err => {
					commit('UPDATE_LOADING', { value: 'fetchClient', remove: true })
					console.log(err);
				})
			}
		},
		updateDevNote({ commit, state }, value) {
			commit('UPDATE_DEV_NOTE', value)
		},
		updateNewFeature({ commit, state }, value) {
			commit('UPDATE_NEW_FEATURE', value)
		},
		updateCustomModuleName({ commit, state }, value) {
			commit('UPDATE_CUSTOM_MODULE_NAME', value)
		},
		updateCustomRouteName({ commit, state }, value) {
			commit('UPDATE_CUSTOM_ROUTE_NAME', value)
		},
		updateOpenDropdown({ commit, state }, value) {
			commit('UPDATE_OPEN_DROPDOWN', value)
		},
		updateShowingOfflineLogBooks({ commit }, value) {
			commit("UPDATE_PROPERTY", { property: 'showingOfflineLogBooks', value: value })
		},
		fetchShoppingCartCount({ state, commit, dispatch }) {
			if (document.location.pathname.indexOf('customer-4473') > -1) return
			dispatch('apiCall', { destination: 'get_shopping_cart_count' }).then(obj => {
				commit('UPDATE_SHOPPING_CART_COUNT', obj.shopping_cart_count)
			}).catch(err => {
				console.log(err);
			})
		},
		fetchFlightDeckMenus({ state, commit, dispatch }) {
			if (document.location.pathname.indexOf('customer-4473') > -1) return
			// collate menu items with menu_item_polices here
			// to allow all menus to use a direct is_allowed and not a function
			if (state.loading.indexOf('fetchFlightDeckMainMenu') == -1) {
				commit('UPDATE_LOADING', { value: 'fetchFlightDeckMainMenu' })
				dispatch('apiCall', { destination: 'get_menu_recursive', data: { menu_code: 'FLIGHT_DECK_MAIN' } }).then(obj => {
					commit('UPDATE_LOADING', { value: 'fetchFlightDeckMainMenu', remove: true })
					for (const [key, menuItem] of Object.entries(obj.menu)) {
						if (menuItem.menu_item_id in state.menu_item_policies && state.menu_item_policies[menuItem.menu_item_id].not_allowed) {
							menuItem.not_allowed = true
						} else {
							menuItem.not_allowed = false
						}
						for (const [subKey, subMenuItem] of Object.entries(menuItem.menu_items)) {
							if (subMenuItem.menu_item_id in state.menu_item_policies && state.menu_item_policies[subMenuItem.menu_item_id].not_allowed) {
								subMenuItem.not_allowed = true
							} else {
								subMenuItem.not_allowed = false
							}
						}
					}
					commit('UPDATE_FLIGHT_DECK_MAIN_MENU', obj.menu)
				}).catch(err => {
					commit('UPDATE_LOADING', { value: 'fetchFlightDeckMainMenu', remove: true })
					console.log(err);
				})
			}
			if (state.loading.indexOf('fetchFlightDeckMetaMenu') == -1) {
				commit('UPDATE_LOADING', { value: 'fetchFlightDeckMetaMenu' })
				dispatch('apiCall', { destination: 'get_menu_recursive', data: { menu_code: 'FLIGHT_DECK_META' } }).then(obj => {
					commit('UPDATE_LOADING', { value: 'fetchFlightDeckMetaMenu', remove: true})
					for (const [key, menuItem] of Object.entries(obj.menu)) {
						if (menuItem.menu_item_id in state.menu_item_policies && state.menu_item_policies[menuItem.menu_item_id].not_allowed) {
							menuItem.not_allowed = true
						} else {
							menuItem.not_allowed = false
						}
					}
					commit('UPDATE_FLIGHT_DECK_META_MENU', obj.menu)
				}).catch(err => {
					commit('UPDATE_LOADING', { value: 'fetchFlightDeckMetaMenu', remove: true})
					console.log(err);
				})
			}
		},
		fetchModuleNavMenu({ state, commit, dispatch }, moduleName) {
			return new Promise((resolve, reject) => {
				dispatch('apiCall', { destination: 'get_menu_recursive', data: { menu_code: moduleName } }).then(obj => {
					if (obj.menu) {
						for (const [key, menuItem] of Object.entries(obj.menu)) {
							if (menuItem.menu_item_id in state.menu_item_policies && state.menu_item_policies[menuItem.menu_item_id].not_allowed) {
								menuItem.not_allowed = true
							} else {
								menuItem.not_allowed = false
							}
						}
						commit('UPDATE_PROPERTY', { property: 'moduleNavMenu', value: obj.menu })
					}
					resolve()
				}).catch(err => {
					console.log(err);
					reject()
				})
			})
		},
		fetchRouteLastRequested({ state, commit, dispatch }) {
			return new Promise((resolve, reject) => {
				dispatch('apiCall', { destination: 'get_route_last_requested' }).then(obj => {
					commit('UPDATE_ROUTE_LAST_REQUESTED', obj.route_last_requested)
					resolve()
				}).catch(err => {
					console.log(err);
					reject()
				})
			})
		},
		setSiteBrand({ state, commit }) {
			switch(state.client.client_type_code) {
				case 'FLIGHT_DECK_LITE':
					commit('UPDATE_SITE_BRAND', 'Flight Deck Lite')
					break;
				case 'FFL_OFFICE':
					commit('UPDATE_SITE_BRAND', 'FFL Office')
					break;
				case 'ADMIN':
					commit('UPDATE_SITE_BRAND', 'Admin')
					break;
				case 'CDS':
				default:
					commit('UPDATE_SITE_BRAND', 'Flight Deck')
					break;
			}
		},
		apiCall({ state, commit, dispatch }, parameters) {
			return new Promise((resolve, reject) => {
				// expected parameters: destination, data, headers, callback
				let url = (parameters.destination.indexOf('.') > 0) ? parameters.destination : state.apiEndpoint + '?action=' + parameters.destination
				let postData = {}
				if ('data' in parameters) {
					postData = JSON.parse(JSON.stringify(parameters.data))
				}
				// if logged in, there will be a dashboard_token cookie
				// don't acquire cookie token if fd_token already passed in parameters
				if (!postData.fd_token) {
					let re = new RegExp("dashboard_token=([^;]+)");
					let cookieToken = re.exec(document.cookie)
					let dashbordToken = (cookieToken != null) ? unescape(cookieToken[1]) : null
					postData.fd_token = dashbordToken
				}
				postData.app_version = state.appVersion
				postData.shopping_cart_id = localStorage.getItem('shopping_cart_id') || ''
				if (!postData.source) {
					postData.source = state.source
				}
				postData.hostname = window.location.hostname
				postData.app_route = document.location.pathname
				// let the API know we're displayed in an iFrame (e.g. for help center from FFL Office)
				if (window.self != window.top) {
					postData.fd_frame = 1
				}
				// let the API know if we're in customer kiosk mode
				postData.kiosk_mode = state.kioskMode
				let defaultHeaders = {
					method: 'post',
					body: JSON.stringify(postData),
					credentials: 'include'
				}
				let headers = parameters.headers || defaultHeaders
				fetch(url, headers).then(function(response) {
					if (!response.ok) {
						return { status: 'error', message: 'The server processing this request either timed out or had a problem running the code.', fatal: true, response: response }
					}
					const contentType = response.headers.get('content-type')
					if (contentType && contentType.includes('application/json')) {
						return response.json();
					} else {
						return { status: 'error', message: 'The server processing this request returned something this program doesn\'t understand.', fatal: false, response: response }
					}
				}).then(obj => {
					if (obj.server_name) {
						commit('UPDATE_SERVER_NAME', obj.server_name)
					}
					if (obj.status == 'login_required' && document.location.pathname.indexOf('login') == -1 && document.location.pathname.indexOf('customer-4473') == -1) {
						localStorage.setItem('return_afer_login', document.location.pathname)
						document.location = '/dashboard/login?login_required=1'
						// commit('UPDATE_LOGIN_REQUIRED', true)
						// document.location = state.loginRoute
					} else {
						if (obj.user) {
							if (obj.user.token) {
								document.cookie = "dashboard_token=" + obj.user.token
								state.dashboardToken = obj.user.token
							}
							// TODO: how do we call getAdministratorFlag here?
							if (obj.user.user_type_code == 'ADMIN' || obj.user.user_type_code == 'SUPERADMIN') {
								state.userHome = '/admin'
							} else {
								state.userHome = '/'
							}
						}
						commit('UPDATE_LOGIN_REQUIRED', false)
						commit('UPDATE_STATE', obj)
						resolve(obj)
					}
				}).catch(error => {
					// console.log(error)
					if (error == 'TypeError: Failed to fetch') {
						// document.location.reload()
					}
					reject(error)
				})
			})
		}
	},
	modules: {
		dashboard_module: DashboardModule,
		orders_sales_module: OrdersSalesModule,
		logbooks: LogBooksModule,
		website_module: WebsiteModule,
		promotions_module: PromotionsModule,
		inventory_module: InventoryModule,
		pos_module: POSModule,
		customers_module: CustomersModule,
		range_memberships_module: RangeMembershipsModule,
		reports_module: ReportsModule
	}
	// strict: developmentEnvironment,
	// plugins: developmentEnvironment? [ createLogger() ] : []
})

export default store

import { mapState, mapGetters, mapActions } from 'vuex'

store.allGetters = Reflect.ownKeys(store.getters)
store.allActions = Reflect.ownKeys(store._actions)

Vue.mixin({
	methods: {
		...mapActions(store.allActions)
	},
	computed: {
		...mapGetters(store.allGetters)
	}
})
